import React from 'react';
import { useIntl } from 'umi';
import classnames from 'classnames';
import { getCountryFlag } from '@/pages/home/_config/countries';
import {
  generateKeyOfMetricsMap,
  generateKeyOfPeriodMetricsMap,
} from '@/pages/intelligence/pages/Compare/PcConsoleGames/utils';
import type { IBaseProps } from '@/pages/intelligence/types/common';
import type { IEntityFieldsMapping, IEntityMetricFieldsMapping } from '../../../types';
import './index.scss';

interface IDoubleMetricsCompareTableBodyProps extends IBaseProps {
  entities: any[];
  entityFieldsMapping: IEntityFieldsMapping;
  periods: string[];
  periodsNamesMapping: {
    [key: string]: string;
  };
  metrics: string[];
  metricsMap: {
    [key: string]: any;
  };
  metricFieldsMapping: IEntityMetricFieldsMapping;
  defaultMetricValueFormatter?: (value: any) => any;
  metricValueFormattersMapping?: {
    [key: string]: (value: any) => any;
  };
  periodMaxMetricsMap: {
    [key: string]: any;
  };
  isOpenHeatMap?: boolean;
  showTopCountries?: boolean;
  placeholder?: string;
}

export default function DoubleMetricsCompareTableBody(props: IDoubleMetricsCompareTableBodyProps) {
  const {
    entities,
    entityFieldsMapping,
    periods,
    periodsNamesMapping,
    metrics,
    metricsMap,
    metricFieldsMapping,
    defaultMetricValueFormatter,
    metricValueFormattersMapping,
    periodMaxMetricsMap,
    isOpenHeatMap,
    showTopCountries = false,
    placeholder = '-',
    style,
    className,
  } = props;

  const { formatMessage } = useIntl();

  const entityIdField = entityFieldsMapping?.entityKey;

  const metricKeyFieldName = metricFieldsMapping?.metricKey;
  const metricValueFieldName = metricFieldsMapping?.metricValue;
  const metricCountriesFieldName = metricFieldsMapping?.countries;

  const getAllDataAboutMetric = (entity: any, period: string, metric: string) => {
    // 根据实体key、时间段、指标创建 key值，从metricsMap中获取 对应的指标对象数据
    const key = generateKeyOfMetricsMap(entity?.[entityIdField], period, metric);
    const metricData = metricsMap?.[key];
    const metricValue = metricData?.[metricValueFieldName];
    const metricValueFormatter = metricValueFormattersMapping?.[metric] || defaultMetricValueFormatter;
    const formattedMetricValue =
      typeof metricValueFormatter === 'function' ? metricValueFormatter(metricValue) : metricValue;
    const periodMetricKey = generateKeyOfPeriodMetricsMap(period, metric);
    const periodMaxMetric = periodMaxMetricsMap?.[periodMetricKey];
    const isMax =
      periodMaxMetric?.[metricKeyFieldName] === metric && periodMaxMetric?.[metricValueFieldName] === metricValue;
    const metricCountriesData = metricData?.[metricCountriesFieldName];

    return {
      // 是否为当前阶段下、当前指标的 最大值
      isMax,
      // 未格式化的指标值
      metricValue,
      // 格式化后的指标值
      formattedMetricValue,
      // 当前阶段下、当前指标的 国家指标数据
      metricCountriesData,
      // 指标value的formatter
      metricValueFormatter,
    };
  };

  return (
    <div
      style={style}
      className={classnames('dbi-entities_table-body double-metrics-table-body', className)}
    >
      {/* 时间段，Last Day、Last Month、Last 3 Month等 */}
      {periods?.map((period) => (
        <div key={period} className="dbi-entities_table-body-row">
          <div className="dbi-entities_table-body-column first-column">
            {formatMessage({ id: periodsNamesMapping?.[period] }) || period || placeholder}
          </div>
          {/* 实体，比如游戏 */}
          {entities?.map((entity) => (
            <div key={entity?.[entityIdField]} className="dbi-entities_table-body-column">
              {/* 指标 */}
              {metrics?.map((metric) => {
                const { isMax, metricValue, formattedMetricValue, metricCountriesData, metricValueFormatter } =
                  getAllDataAboutMetric(entity, period, metric);
                return (
                  <div
                    key={metric}
                    className={classnames('dbi-entities_table-body-child-column', {
                      // 指标高亮样式
                      'is-highlight': isOpenHeatMap && isMax,
                      'is-no-data': !metricValue,
                    })}
                  >
                    <div className="dbi-entities_metric">
                      {formattedMetricValue || placeholder}
                    </div>
                    {/* Top Countries */}
                    {showTopCountries && (
                      <div
                        className={classnames('dbi-entities_country-list-container', {
                          'has-mb': metricCountriesData?.length > 0,
                        })}
                      >
                        <ul className="dbi-entities_country-list">
                          {metricCountriesData?.map((metricCountryData: any) => {
                            const countryMetricValue = metricCountryData?.value;
                            const formattedCountryMetricsValue =
                              typeof metricValueFormatter === 'function'
                                ? metricValueFormatter(countryMetricValue)
                                : countryMetricValue;
                            return (
                              <li
                                key={metricCountryData?.market}
                                className="dbi-entities_country-item"
                              >
                                <div>
                                  <img
                                    className="dbi-entities_country-flag"
                                    src={getCountryFlag(metricCountryData?.market_abbr)}
                                    alt=""
                                  />
                                  <span className="dbi-entities_country-name">
                                    {metricCountryData?.market || placeholder}
                                  </span>
                                </div>
                                <div className="dbi-entities_country-metric-value">
                                  {formattedCountryMetricsValue || placeholder}
                                </div>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}
